import React, { forwardRef, useCallback, useEffect, useMemo, useRef } from "react";
import { Virtuoso } from "react-virtuoso";
import NewsCard from "./NewsCard";
import RegularAdCard from "../ads/RegularAdCard";


const SanitizedVirtuosoList = forwardRef(function SanitizedVirtuosoList(
  { style, context: _context, ...domProps },
  ref
) {
  const safeStyle = style ? { ...style } : undefined;

  if (safeStyle) {
    if (
      typeof safeStyle.paddingBottom === "number" &&
      !Number.isFinite(safeStyle.paddingBottom)
    ) {
      safeStyle.paddingBottom = 0;
    }

    if (
      typeof safeStyle.paddingTop === "number" &&
      !Number.isFinite(safeStyle.paddingTop)
    ) {
      safeStyle.paddingTop = 0;
    }
  }

  return <div ref={ref} {...domProps} style={safeStyle} />;
});

const SanitizedVirtuosoScroller = forwardRef(function SanitizedVirtuosoScroller(
  { style, context: _context, ...domProps },
  ref
) {
  const safeStyle = style ? { ...style } : undefined;

  if (
    safeStyle &&
    typeof safeStyle.height === "number" &&
    !Number.isFinite(safeStyle.height)
  ) {
    // Virtuoso derives this value from totalListHeight + deviation. During an
    // initial measurement pass the derived number can be non-finite. Omitting
    // only that transient invalid property matches the browser's effective
    // behavior for NaN while avoiding React's invalid-style console error.
    delete safeStyle.height;
  }

  return <div ref={ref} {...domProps} style={safeStyle} />;
});

const INCREASE_VIEWPORT_BY = { top: 900, bottom: 1400 };
const OVERSCAN = { main: 600, reverse: 400 };
const LOAD_MORE_THRESHOLD = 1000;
const LOAD_MORE_THROTTLE_MS = 500;

export const NewsCardLoadSkeleton = () => (
  <article
    className="news-card news-card--load-skeleton"
    aria-hidden="true"
  >
    <div className="news-card__content news-card-load-skeleton__content">
      <span className="news-card-load-skeleton__title news-card-load-skeleton__shine" />
      <span className="news-card-load-skeleton__title news-card-load-skeleton__title--short news-card-load-skeleton__shine" />
      <span className="news-card-load-skeleton__text news-card-load-skeleton__shine" />
      <span className="news-card-load-skeleton__text news-card-load-skeleton__text--short news-card-load-skeleton__shine" />
      <span className="news-card-load-skeleton__meta news-card-load-skeleton__shine" />
    </div>
    <span className="news-card__image-wrapper news-card-load-skeleton__image news-card-load-skeleton__shine" />
  </article>
);

const getNewsKey = (item, index) => {
  if (item?.id !== undefined && item?.id !== null) return `id:${item.id}`;
  if (item?.link) return `link:${item.link}`;

  return `fallback:${index}`;
};

const VirtualizedNewsList = ({
  items = [],
  favicons = {},
  onMarkAsRead,
  onNewsOpen,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  scrollParent,
  scrollToTopSignal = 0,
  scrollTarget,
  onScrollTargetHandled,
  onAtTopChange,
  savedLinks,
  onToggleSaved,
  onShare,
  onView,
  footerSpacerHeight = 0,
  mobileDividerEdge = "top",
  onRangeChanged,
  eagerImages = false,
  initialItemCount = 0,
  headerContent = null,
  regularAdsEvery = 0,
  regularAds = [],
  houseAd = null,
  adSurface = "home",
  adDevice = "desktop",
  adViewabilityEnabled = true,
  adAnalyticsSlotPrefix = "",
}) => {
  const virtuosoRef = useRef(null);
  const loadMoreRef = useRef(onLoadMore);
  const isLoadingMoreRef = useRef(isLoadingMore);
  const lastLoadMoreAtRef = useRef(0);
  useEffect(() => {
    loadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  useEffect(() => {
    if (!scrollToTopSignal) return;

    /*
    |-----------------------------------------------------------------------
    | Scroll to the real start of the feed
    |-----------------------------------------------------------------------
    |
    | In mobile Focus, the First Ad is rendered as Virtuoso's Header.
    | `scrollToIndex({ index: 0 })` aligns the first NEWS item with the top
    | and leaves that Header above the viewport. When a custom scroll parent
    | owns the vertical scroll, move that parent to its true top instead so
    | the First Ad is visible above the first news card.
    |
    */
    if (scrollParent) {
      scrollParent.scrollTo({
        top: 0,
        behavior: "auto",
      });
      return;
    }

    virtuosoRef.current?.scrollToIndex({
      index: 0,
      align: "start",
      behavior: "auto",
    });
  }, [scrollParent, scrollToTopSignal]);

  useEffect(() => {
    if (!scrollTarget) return;

    const targetIndex = items.findIndex((item) => {
      const sameId =
        scrollTarget.id !== undefined &&
        scrollTarget.id !== null &&
        String(item?.id) === String(scrollTarget.id);

      const sameLink =
        Boolean(scrollTarget.link) &&
        item?.link === scrollTarget.link;

      return sameId || sameLink;
    });

    if (targetIndex < 0) return;

    virtuosoRef.current?.scrollToIndex({
      index: targetIndex,
      align: "center",
      behavior: "smooth",
    });

    let frameId = null;
    let attempts = 0;

    const finishTargetScroll = () => {
      const ids = [];

      if (scrollTarget.link) {
        ids.push(`news-${encodeURIComponent(scrollTarget.link)}`);
      }

      if (scrollTarget.id !== undefined && scrollTarget.id !== null) {
        ids.push(`news-${encodeURIComponent(String(scrollTarget.id))}`);
      }

      const element = ids
        .map((id) => document.getElementById(id))
        .find(Boolean);

      if (!element && attempts < 12) {
        attempts += 1;
        frameId = window.requestAnimationFrame(finishTargetScroll);
        return;
      }

      if (element) {
        element.classList.add("news-card--highlight");

        window.setTimeout(() => {
          element.classList.remove("news-card--highlight");
        }, 2000);
      }

      onScrollTargetHandled?.(scrollTarget);
    };

    frameId = window.requestAnimationFrame(finishTargetScroll);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [items, onScrollTargetHandled, scrollTarget]);

  const requestMore = useCallback(() => {
    if (
      !hasMore ||
      !loadMoreRef.current ||
      isLoadingMoreRef.current
    ) {
      return;
    }

    const now = Date.now();

    if (
      now - lastLoadMoreAtRef.current <
      LOAD_MORE_THROTTLE_MS
    ) {
      return;
    }

    lastLoadMoreAtRef.current = now;
    loadMoreRef.current();
  }, [hasMore]);

  const requestMoreIfNearEnd = useCallback(() => {
    if (
      typeof window === "undefined" ||
      typeof document === "undefined"
    ) {
      return;
    }

    const scroller =
      scrollParent ||
      document.scrollingElement ||
      document.documentElement;

    if (!scroller) return;

    const remaining =
      scroller.scrollHeight -
      scroller.scrollTop -
      scroller.clientHeight;

    if (remaining < LOAD_MORE_THRESHOLD) {
      requestMore();
    }
  }, [requestMore, scrollParent]);

  useEffect(() => {
    if (!hasMore) return;
    if (typeof window === "undefined") return;

    requestMoreIfNearEnd();

    const scroller = scrollParent || window;

    scroller.addEventListener("scroll", requestMoreIfNearEnd, {
      passive: true,
    });

    window.addEventListener("resize", requestMoreIfNearEnd);

    return () => {
      scroller.removeEventListener("scroll", requestMoreIfNearEnd);
      window.removeEventListener("resize", requestMoreIfNearEnd);
    };
  }, [hasMore, items.length, requestMoreIfNearEnd, scrollParent]);

  const Header = useCallback(
    () => headerContent,
    [headerContent]
  );

  const Footer = useCallback(
    () => (
      <>
        {/*
        | Keep one stable skeleton card at the end of every feed while more
        | news may still exist. This avoids switching between a text button
        | and a skeleton when an offline Load More request fails or retries.
        */}
        {hasMore && <NewsCardLoadSkeleton />}

        {footerSpacerHeight > 0 && (
          <div
            className="virtualized-news-list__footer-spacer"
            style={{ height: footerSpacerHeight }}
            aria-hidden="true"
          />
        )}
      </>
    ),
    [footerSpacerHeight, hasMore]
  );

  const components = useMemo(
    () => ({
      Header,
      Footer,
      List: SanitizedVirtuosoList,
      Scroller: SanitizedVirtuosoScroller,
    }),
    [Footer, Header]
  );


  const renderItem = useCallback(
    (index, item) => {
      const newsPosition = index + 1;
      const hasRegularAdAfterNews =
        regularAdsEvery > 0 &&
        newsPosition % regularAdsEvery === 0;
      const regularAdIndex = hasRegularAdAfterNews
        ? Math.floor(newsPosition / regularAdsEvery) - 1
        : -1;
      const regularDecisionPrepared =
        regularAdIndex >= 0 && regularAdIndex < regularAds.length;
      const regularAd = regularDecisionPrepared
        ? regularAds[regularAdIndex] ?? null
        : null;

      return (
        <>
          <NewsCard
            news={item}
            favicon={favicons[item.source]}
            onVisible={
              item?.link && onMarkAsRead
                ? () => {
                    onMarkAsRead(item);
                  }
                : undefined
            }
            onOpen={onNewsOpen}
            isSaved={item?.link ? savedLinks?.has(item.link) : false}
            onToggleSaved={onToggleSaved}
            onShare={onShare}
            onView={onView}
            mobileDividerEdge={mobileDividerEdge}
            eagerImageLoading={eagerImages}
          />

          {hasRegularAdAfterNews &&
            regularDecisionPrepared &&
            (regularAd || houseAd?.regularEnabled === true) && (
              <RegularAdCard
                ad={regularAd}
                houseAd={houseAd}
                mobileDividerEdge={mobileDividerEdge}
                surface={adSurface}
                device={adDevice}
                analyticsSlot={`${
                  adAnalyticsSlotPrefix
                    ? `${adAnalyticsSlotPrefix}:`
                    : ""
                }regular:${regularAdIndex}`}
                viewabilityEnabled={adViewabilityEnabled}
              />
            )}
        </>
      );
    },
    [
      eagerImages,
      favicons,
      mobileDividerEdge,
      onMarkAsRead,
      onNewsOpen,
      onShare,
      onToggleSaved,
      onView,
      regularAds,
      regularAdsEvery,
      houseAd,
      adAnalyticsSlotPrefix,
      adDevice,
      adSurface,
      adViewabilityEnabled,
      savedLinks,
    ]
  );

  return (
    <Virtuoso
      ref={virtuosoRef}
      className="virtualized-news-list"
      data={items}
      components={components}
      customScrollParent={scrollParent || undefined}
      useWindowScroll={!scrollParent}
      endReached={hasMore ? requestMore : undefined}
      atTopStateChange={onAtTopChange}
      increaseViewportBy={INCREASE_VIEWPORT_BY}
      overscan={OVERSCAN}
      computeItemKey={(index, item) => getNewsKey(item, index)}
      itemContent={renderItem}
      rangeChanged={onRangeChanged}
      initialItemCount={initialItemCount || undefined}
    />
  );
};

export default VirtualizedNewsList;
